/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Download, FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CheckStatus, DeclarationCheck, DeclarationStatus, UploadStatus } from '@/api/areas/declarations';
import { Badge, type Tone } from '@/components/Badge';
import { Callout, DataTable, Field, type Column } from '@/components/blocks';
import { WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { checkLabelWith } from '@/lib/checkLabel';
import { ActionDialog, FormActions } from '../compliance/ui';

const CHECK_TONE: Record<CheckStatus, Tone> = { PASS: 'ok', FAIL: 'crit', UNKNOWN: 'warn' };

export const DECLARATION_TONE: Record<DeclarationStatus, Tone> = {
  GENERATED: 'neutral',
  UPLOADED: 'pending',
  VERIFIED: 'ok',
  REVIEW_REQUIRED: 'warn',
  APPROVED: 'ok',
  REJECTED: 'crit',
};

export const UPLOAD_TONE: Record<UploadStatus, Tone> = {
  PENDING: 'pending',
  VERIFIED: 'ok',
  REVIEW_REQUIRED: 'warn',
  APPROVED: 'ok',
  REJECTED: 'crit',
  SUPERSEDED: 'neutral',
};

/** A check in words wherever its code is shown (lib/checkLabel.ts; `FIELD:<key>` shows the field's label). */
export function useCheckLabel() {
  const { t, i18n } = useTranslation('declarations');
  const tr = t as unknown as (key: string, values?: Record<string, unknown>) => string;
  return (c: { code: string; detail?: Record<string, unknown> | null }): string =>
    checkLabelWith(c.code, c.detail, (key, values) => (i18n.exists(key, { ns: 'declarations' }) ? tr(key, values) : undefined));
}

/** The per-check table: pass / fail / unknown with the reason (masked values only). */
export function CheckTable({ checks }: { checks: DeclarationCheck[] }) {
  const { t } = useTranslation('declarations');
  const label = useCheckLabel();
  const failing = checks.filter((c) => c.status !== 'PASS').length;
  const columns: Column<DeclarationCheck>[] = [
    { key: 'check', header: t('checks.check'), cell: (c) => <span title={c.code}>{label(c)}</span> },
    {
      key: 'result',
      header: t('checks.result'),
      cell: (c) => (
        <Badge tone={CHECK_TONE[c.status]} title={c.status}>
          {t(`checkStatus.${c.status}`)}
        </Badge>
      ),
    },
    {
      key: 'reason',
      header: t('checks.reason'),
      cell: (c) => (
        <span className="grid gap-1 text-xs">
          <span>
            {c.status !== 'PASS' ? <span className="font-mono">{c.reason}</span> : null}{' '}
            <span className="text-muted-foreground">{c.reason === 'NO_TIMESTAMP' ? t('checks.noTimestamp') : c.message}</span>
          </span>
          {c.code.startsWith('SIGNATURE:') ? <SignatureLine detail={c.detail ?? {}} /> : null}
        </span>
      ),
    },
  ];
  return (
    <div className="grid gap-3">
      {checks.length ? <Callout tone={failing ? 'warn' : 'ok'}>{failing ? t('checks.attention', { count: failing }) : t('checks.allPass')}</Callout> : null}
      <DataTable columns={columns} rows={checks} rowKey={(c) => c.code} empty={<p className="p-4 text-sm text-muted-foreground">{t('checks.empty')}</p>} />
    </div>
  );
}

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/** One signature / document time-stamp of an upload: who, which CA, when, what it covers. */
function SignatureLine({ detail }: { detail: Record<string, unknown> }) {
  const { t } = useTranslation('declarations');
  const ts = (detail.timestamp ?? {}) as Record<string, unknown>;
  const hints = Array.isArray(detail.signer_hints) ? (detail.signer_hints as string[]).join(', ') : null;
  const range = Array.isArray(detail.byte_range) ? (detail.byte_range as number[]).join(' ') : null;
  const items: [string, string | null][] = [
    [t('sig.signer'), [str(detail.signer_name) ?? str(detail.signer_name_hint), hints].filter(Boolean).join(' · ') || null],
    [t('sig.ca'), str(detail.ca)],
    [t('sig.signingTime'), str(detail.signing_time)],
    [
      t('sig.timestamp'),
      ts.present ? `${ts.valid ? t('sig.timestampValid') : t('sig.timestampInvalid')}${str(ts.time) ? ` · ${str(ts.time)}` : ''}${str(ts.tsa) ? ` · ${str(ts.tsa)}` : ''}` : t('sig.noTimestamp'),
    ],
    [t('sig.covers'), detail.covers_whole_document ? t('sig.wholeDocument') : t('sig.partOfDocument')],
    [t('sig.byteRange'), range],
    [t('sig.valid'), detail.valid === true ? t('sig.yes') : detail.valid === false ? t('sig.no') : null],
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-muted-foreground">
      {items
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <div key={k} className="contents">
            <dt>{k}</dt>
            <dd className="break-all text-foreground">{v}</dd>
          </div>
        ))}
    </dl>
  );
}

/** An object URL for a blob, revoked when it changes or the component unmounts. */
export function useObjectUrl(blob: Blob | null): string | null {
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  return url;
}

/** A PDF or image shown inline (never executed: the browser's viewer renders it). */
export function FileFrame({ blob, title }: { blob: Blob | null; title: string }) {
  const url = useObjectUrl(blob);
  if (!url || !blob) return null;
  return blob.type.startsWith('image/') ? (
    <img src={url} alt={title} className="max-h-[70vh] w-full rounded-md border object-contain" />
  ) : (
    <object data={url} type="application/pdf" aria-label={title} className="h-[70vh] w-full rounded-md border">
      <a href={url} download={`${title}.pdf`} className="text-primary underline">
        {title}
      </a>
    </object>
  );
}

/**
 * Asks for the reason of an audited read, then loads the file with it.
 * `load` returns the blob; errors (no permission) are shown in the dialog.
 */
export function ReadFileDialog({ open, onOpenChange, onLoaded, load, title, description }: { open: boolean; onOpenChange: (o: boolean) => void; onLoaded: (b: Blob) => void; load: (reason: string) => Promise<Blob>; title?: string; description?: string }) {
  const { t } = useTranslation('declarations');
  const { t: tc } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <ActionDialog open={open} onOpenChange={onOpenChange} title={title ?? t('read.title')} description={description ?? t('read.description')}>
      <form
        className="grid gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!reason.trim()) return;
          setBusy(true);
          setError(null);
          try {
            onLoaded(await load(reason.trim()));
            onOpenChange(false);
          } catch (err) {
            setError(err);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label={t('read.reason')} hint={t('read.reasonHint')}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
        </Field>
        {error ? <WriteError error={error} /> : null}
        <FormActions>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {tc('action.cancel')}
          </Button>
          <Button type="submit" disabled={busy || !reason.trim()}>
            <FileText /> {t('read.open')}
          </Button>
        </FormActions>
      </form>
    </ActionDialog>
  );
}

/** Base64 of a file (the admin API takes JSON bodies). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = typeof r.result === 'string' ? r.result : '';
      resolve(s.slice(s.indexOf(',') + 1));
    };
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(file);
  });
}

/** Saves a blob as a file named `name`. */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A PDF (the browser's viewer) or an image, from a blob fetched with a reason. */
export function FilePreview({ blob, name }: { blob: Blob; name: string }) {
  const url = useObjectUrl(blob);
  if (!url) return null;
  return blob.type.startsWith('image/') ? (
    <img src={url} alt={name} className="max-h-[75vh] w-full rounded border object-contain" />
  ) : (
    // A blob: URL of a PDF: a sandboxed frame would block the browser's PDF viewer.
    // oxlint-disable-next-line react/iframe-missing-sandbox
    <iframe src={url} title={name} className="h-[75vh] w-full rounded border" />
  );
}

/** A file to open: its name (what it is saved as) and whether it is downloaded or previewed. */
export interface FileTarget {
  name: string;
  download: boolean;
}

/**
 * "Xem" / "Tải về" a stored file: asks for the reason of the audited read, then shows the file
 * in a drawer (PDF / image) with "Tải về", or saves it. `load` fetches it with the reason.
 */
export function FileViewer({
  target,
  onClose,
  load,
  title,
  description,
  hint,
  downloadLabel,
  marker,
}: {
  target: FileTarget | null;
  onClose: () => void;
  load: (reason: string, target: FileTarget) => Promise<Blob>;
  title: string;
  description: string;
  hint: string;
  downloadLabel: string;
  /** `data-*` attribute of the drawer (smoke tests). */
  marker: string;
}) {
  const [preview, setPreview] = useState<{ name: string; blob: Blob } | null>(null);
  return (
    <>
      <ReadFileDialog
        open={target !== null}
        onOpenChange={(o) => (o ? undefined : onClose())}
        title={title}
        description={description}
        load={(reason) => (target ? load(reason, target) : Promise.reject(new Error('no file')))}
        onLoaded={(blob) => {
          if (!target) return;
          if (target.download) saveBlob(blob, target.name);
          else setPreview({ name: target.name, blob });
        }}
      />
      <Sheet open={preview !== null} onOpenChange={(o) => (o ? undefined : setPreview(null))}>
        <SheetContent side="right" className="w-full gap-0 sm:max-w-3xl" {...{ [`data-${marker}`]: '' }}>
          <SheetHeader>
            <SheetTitle className="break-all">{preview?.name}</SheetTitle>
            <SheetDescription>{hint}</SheetDescription>
          </SheetHeader>
          {preview ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
              <FilePreview blob={preview.blob} name={preview.name} />
              <div>
                <Button variant="outline" size="sm" onClick={() => saveBlob(preview.blob, preview.name)} data-file-preview-download>
                  <Download /> {downloadLabel}
                </Button>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
